import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errorMessage } from "@/lib/api";

const usd = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString("en-US")}`);
const inputCls = "h-11 rounded-md border-input bg-white text-[16px]";

const ValueResult = ({ r }) => (
  <div className="mt-6 animate-rise-in" data-testid="home-value-result">
    {r.estimate ? (
      <>
        <p className="text-[13px] text-neutral-600">{r.address}</p>
        <p className="mt-1 font-display text-5xl text-ink" data-testid="home-value-amount">{usd(r.estimate)}</p>
        <p className="mt-1 text-[14px] text-neutral-600">Likely range {usd(r.low)} to {usd(r.high)}</p>
        {r.comparables?.length > 0 && (
          <ul className="mt-5 divide-y divide-neutral-200 text-[14px]" data-testid="home-value-comps">
            {r.comparables.map((c, i) => (
              <li key={`${c.address}-${i}`} className="flex flex-wrap justify-between gap-2 py-2">
                <span className="min-w-0 text-neutral-700">{c.address}{c.miles != null && <span className="text-neutral-500"> · {c.miles} mi</span>}</span>
                <span className="font-medium text-ink">{usd(c.price)}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-4 rounded-md bg-mist p-3 text-[14px] text-neutral-700">Rob's team will follow up with a precise valuation that counts your upgrades and condition. Automated estimates (RentCast) aren't appraisals.</p>
      </>
    ) : (
      <p className="rounded-md bg-mist p-4 text-[15px] text-neutral-700" data-testid="home-value-manual">{r.message}</p>
    )}
  </div>
);

/** Lead-gen home value: step 1 address, step 2 contact details (required), then the instant estimate. Address can be prefilled from ?street=&city=&zip=. */
export const HomeValueTool = () => {
  const [params] = useSearchParams();
  const pre = { street: params.get("street") || "", city: params.get("city") || "", zip: params.get("zip") || "" };
  const [a, setA] = useState(pre);
  const [c, setC] = useState({ first_name: "", last_name: "", email: "", phone: "", consent: false });
  const [step, setStep] = useState(pre.street && pre.city && /^\d{5}$/.test(pre.zip) ? "contact" : "address");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const setAddr = (k) => (e) => setA((s) => ({ ...s, [k]: e.target.value }));
  const setCon = (k) => (e) => setC((s) => ({ ...s, [k]: e.target.value }));

  const next = (e) => {
    e.preventDefault();
    if (!a.street.trim() || !a.city.trim() || !/^\d{5}$/.test(a.zip)) return setErr("Please enter the street, city and a 5-digit ZIP.");
    setErr("");
    setStep("contact");
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!c.consent) return setErr("Please agree to be contacted so we can send your valuation.");
    setBusy(true); setErr("");
    try {
      const r = await api.post("/estimate/value-lead", { ...a, ...c });
      setRes(r.data ?? {});
      setStep("done");
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-neutral-200 bg-white p-6 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.35)] sm:p-8" data-testid="home-value-tool">
      <p className="flex items-center gap-2 text-lg font-medium text-ink"><Sparkles className="h-5 w-5 text-brass" /> Instant home value estimate</p>
      <p className="mt-1 text-[14px] text-neutral-600">Step {step === "address" ? 1 : 2} of 2 · {step === "address" ? "Your home's address" : step === "contact" ? "Where should we send your valuation?" : "Your estimate"}</p>
      {step === "address" && (
        <form onSubmit={next} className="mt-4 grid gap-3 sm:grid-cols-[1.6fr_1fr_0.7fr]" data-testid="home-value-address-form">
          <Input className={inputCls} placeholder="Street address" value={a.street} onChange={setAddr("street")} autoComplete="address-line1" data-testid="home-value-street" />
          <Input className={inputCls} placeholder="City" value={a.city} onChange={setAddr("city")} autoComplete="address-level2" data-testid="home-value-city" />
          <Input className={inputCls} placeholder="ZIP" inputMode="numeric" maxLength={5} value={a.zip} onChange={setAddr("zip")} autoComplete="postal-code" data-testid="home-value-zip" />
          <button type="submit" data-sfx="chime" data-testid="home-value-next" className="h-12 rounded-full bg-ink px-6 text-[15px] font-medium text-white transition-colors hover:bg-neutral-800 sm:col-span-3">See my home's value</button>
        </form>
      )}
      {step === "contact" && (
        <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="home-value-contact-form">
          <p className="text-[14px] text-ink sm:col-span-2">{a.street}, {a.city}, VA {a.zip} <button type="button" onClick={() => setStep("address")} className="ml-2 inline-flex items-center gap-1 text-neutral-500 underline underline-offset-4" data-testid="home-value-edit-address"><ArrowLeft className="h-3.5 w-3.5" /> Edit</button></p>
          <Input className={inputCls} required placeholder="First name" value={c.first_name} onChange={setCon("first_name")} autoComplete="given-name" data-testid="home-value-first-name" />
          <Input className={inputCls} placeholder="Last name" value={c.last_name} onChange={setCon("last_name")} autoComplete="family-name" data-testid="home-value-last-name" />
          <Input className={inputCls} required type="email" placeholder="Email" value={c.email} onChange={setCon("email")} autoComplete="email" data-testid="home-value-email" />
          <Input className={inputCls} required type="tel" minLength={7} placeholder="Phone" value={c.phone} onChange={setCon("phone")} autoComplete="tel" data-testid="home-value-phone" />
          <label className="flex items-start gap-2 text-[13px] text-neutral-600 sm:col-span-2">
            <Checkbox checked={c.consent} onCheckedChange={(v) => setC((s) => ({ ...s, consent: v === true }))} data-testid="home-value-consent" className="mt-0.5" />
            I agree The Rob Resh Team may contact me about my home's value.
          </label>
          <button type="submit" disabled={busy} data-sfx="chime" data-testid="home-value-submit"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-ink px-6 text-[15px] font-medium text-white transition-colors hover:bg-neutral-800 disabled:opacity-60 sm:col-span-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Show my estimate
          </button>
          <p className="flex items-center gap-1.5 text-[12px] text-neutral-500 sm:col-span-2"><Lock className="h-3.5 w-3.5" /> Your details go only to The Rob Resh Team. No spam, ever.</p>
        </form>
      )}
      {err && <p className="mt-4 rounded-md bg-red-50 p-3 text-[14px] text-red-700" data-testid="home-value-error">{err}</p>}
      {step === "done" && res && <ValueResult r={res} />}
    </div>
  );
};
